import { type LlmSpendDashboardResponse } from '@libraries/typescript/api_client';

type LegacyTotals = LlmSpendDashboardResponse['totals'] & {
    readonly active_organization_count?: number;
    readonly active_user_count?: number;
};

type LegacyBucket = LlmSpendDashboardResponse['buckets'][number] & LegacyTotals;

type LegacyOutcome = {
    readonly outcome: string;
    readonly outreach_count: number;
    readonly filled_shift_count: number;
    readonly tracked_estimated_spend_usd: number;
};

type LegacyOutlier = LlmSpendDashboardResponse['outliers'][number] & {
    readonly outcome?: string;
};

type LegacyResponseOutcome = Omit<
    NonNullable<
        LlmSpendDashboardResponse['outreach_response_outcomes']
    >[number],
    'shift_count'
> & {
    readonly shift_count?: number;
};

type DashboardBucket = LlmSpendDashboardResponse['buckets'][number];
type OutreachGenerationMetrics =
    DashboardBucket['outreach_by_generation'][string];
type SchedulerBucket = LlmSpendDashboardResponse['scheduler_buckets'][number];
type SchedulerGenerationMetrics = SchedulerBucket['by_generation'][string];

type GenerationMetricsWithOptionalWakes = {
    readonly wakes_by_objective?: Readonly<Record<string, number>>;
};

type NormalizedGenerationMetrics<T extends GenerationMetricsWithOptionalWakes> =
    Omit<T, 'wakes_by_objective'> & {
        readonly wakes_by_objective: Record<string, number>;
    };

export type NormalizedLlmSpendBucket = Omit<
    DashboardBucket,
    'wakes_by_objective' | 'outreach_by_generation'
> & {
    readonly wakes_by_objective: Record<string, number>;
    readonly outreach_by_generation: Record<
        string,
        NormalizedGenerationMetrics<OutreachGenerationMetrics>
    >;
};

export type NormalizedLlmSpendSchedulerBucket = Omit<
    SchedulerBucket,
    'wakes_by_objective' | 'by_generation'
> & {
    readonly wakes_by_objective: Record<string, number>;
    readonly by_generation: Record<
        string,
        NormalizedGenerationMetrics<SchedulerGenerationMetrics>
    >;
};

type NormalizedCoverage = Omit<
    LlmSpendDashboardResponse['coverage'],
    'native_history_complete'
> & {
    /** Null while an older API version cannot report native-rollup state. */
    readonly native_history_complete: boolean | null;
};

export type NormalizedLlmSpendDashboardResponse = Omit<
    LlmSpendDashboardResponse,
    | 'buckets'
    | 'coverage'
    | 'outreach_response_outcomes'
    | 'providers'
    | 'scheduler_buckets'
> & {
    readonly buckets: Array<NormalizedLlmSpendBucket>;
    readonly coverage: NormalizedCoverage;
    readonly outreach_response_outcomes: NonNullable<
        LlmSpendDashboardResponse['outreach_response_outcomes']
    >;
    readonly scheduler_buckets: Array<NormalizedLlmSpendSchedulerBucket>;
    // Null while an older API task is still serving this route, and for
    // every section other than the overview.
    readonly providers: NonNullable<
        LlmSpendDashboardResponse['providers']
    > | null;
};

type CoverageMode = LlmSpendDashboardResponse['coverage']['modes'][number];
type NativeModeField =
    | 'native_history_start'
    | 'native_source_watermark'
    | 'native_last_completed_at';

type LegacyCoverageMode = Omit<CoverageMode, NativeModeField> &
    Partial<Pick<CoverageMode, NativeModeField>>;

type LegacyCoverage = Omit<
    LlmSpendDashboardResponse['coverage'],
    'native_history_complete' | 'modes'
> & {
    readonly native_history_complete?: boolean;
    readonly modes: ReadonlyArray<LegacyCoverageMode>;
};

type CompatibleDashboardResponse = Omit<
    LlmSpendDashboardResponse,
    | 'coverage'
    | 'totals'
    | 'previous_totals'
    | 'buckets'
    | 'outreach_response_outcomes'
    | 'outreach_statuses'
    | 'scheduler_totals'
    | 'scheduler_buckets'
    | 'scheduler_top_organizations'
    | 'scheduler_top_users'
    | 'scheduler_outliers'
    | 'providers'
> & {
    readonly coverage: LegacyCoverage;
    readonly totals: LegacyTotals;
    readonly previous_totals: LegacyTotals;
    readonly buckets: Array<LegacyBucket>;
    readonly outreach_response_outcomes?: Array<LegacyResponseOutcome>;
    readonly outreach_statuses?: LlmSpendDashboardResponse['outreach_statuses'];
    readonly outreach_outcomes?: Array<LegacyOutcome>;
    readonly scheduler_totals?: LlmSpendDashboardResponse['scheduler_totals'];
    readonly scheduler_buckets?: LlmSpendDashboardResponse['scheduler_buckets'];
    readonly scheduler_top_organizations?: LlmSpendDashboardResponse['scheduler_top_organizations'];
    readonly scheduler_top_users?: LlmSpendDashboardResponse['scheduler_top_users'];
    readonly scheduler_outliers?: LlmSpendDashboardResponse['scheduler_outliers'];
    readonly providers?: LlmSpendDashboardResponse['providers'];
};

function normalizeTotals<T extends LegacyTotals>(totals: T) {
    return {
        ...totals,
        tracked_organization_count:
            totals.tracked_organization_count ??
            totals.active_organization_count ??
            0,
        tracked_user_count:
            totals.tracked_user_count ?? totals.active_user_count ?? 0,
    };
}

function normalizeGenerationMetrics<
    T extends GenerationMetricsWithOptionalWakes,
>(
    generations: Readonly<Record<string, T>> | null | undefined,
): Record<string, NormalizedGenerationMetrics<T>> {
    return Object.fromEntries(
        Object.entries(generations ?? {}).map(([generation, metrics]) => [
            generation,
            {
                ...metrics,
                wakes_by_objective: metrics.wakes_by_objective ?? {},
            },
        ]),
    );
}

const EMPTY_SCHEDULER_TOTALS = {
    tracked_estimated_spend_usd: 0,
    conversation_count: 0,
    llm_call_count: 0,
    wake_count: 0,
};

/** Keep the dashboard usable while web and API deploys overlap. */
export function normalizeLlmSpendDashboardResponse(
    response: LlmSpendDashboardResponse,
): NormalizedLlmSpendDashboardResponse {
    // A widening, not a narrowing: every legacy field is optional, so the
    // current response satisfies the compatible type without a cast.
    const compatible: CompatibleDashboardResponse = response;
    return {
        ...response,
        coverage: {
            ...compatible.coverage,
            // An API that predates the native rollup cannot report whether
            // its history is complete. Keep that distinct from a real,
            // active history build.
            native_history_complete:
                compatible.coverage.native_history_complete ?? null,
            // Its per-mode entries omit the native checkpoint entirely.
            // Spell the absence as null rather than letting the normalized
            // type promise fields the response never carried.
            modes: compatible.coverage.modes.map((row) => ({
                ...row,
                native_history_start: row.native_history_start ?? null,
                native_source_watermark: row.native_source_watermark ?? null,
                native_last_completed_at: row.native_last_completed_at ?? null,
            })),
        },
        totals: normalizeTotals(compatible.totals),
        previous_totals: normalizeTotals(compatible.previous_totals),
        buckets: compatible.buckets.map((row) => ({
            ...normalizeTotals(row),
            spend_by_model_component: row.spend_by_model_component ?? {},
            wakes_by_objective: row.wakes_by_objective ?? {},
            outreach_by_generation: normalizeGenerationMetrics(
                row.outreach_by_generation,
            ),
        })),
        outreach_response_outcomes: (
            compatible.outreach_response_outcomes ?? []
        ).map((row) => ({
            ...row,
            // An older API cannot supply a truthful denominator, so keep its
            // rows out of the tracked response buckets during rollout.
            outcome: row.shift_count === undefined ? 'untracked' : row.outcome,
            shift_count: row.shift_count ?? 0,
        })),
        outreach_statuses:
            compatible.outreach_statuses?.map((row) => ({
                ...row,
                shift_count: row.shift_count ?? 0,
                filled_shift_count: row.filled_shift_count ?? 0,
            })) ??
            compatible.outreach_outcomes?.map((row) => ({
                status: row.outcome,
                outreach_count: row.outreach_count,
                shift_count: 0,
                filled_shift_count: row.filled_shift_count,
                tracked_estimated_spend_usd: row.tracked_estimated_spend_usd,
            })) ??
            [],
        scheduler_totals: compatible.scheduler_totals ?? EMPTY_SCHEDULER_TOTALS,
        scheduler_buckets: (compatible.scheduler_buckets ?? []).map((row) => ({
            ...row,
            wakes_by_objective: row.wakes_by_objective ?? {},
            by_generation: normalizeGenerationMetrics(row.by_generation),
        })),
        scheduler_top_organizations:
            compatible.scheduler_top_organizations ?? [],
        scheduler_top_users: compatible.scheduler_top_users ?? [],
        scheduler_outliers: compatible.scheduler_outliers ?? [],
        providers: compatible.providers ?? null,
        top_organizations: compatible.top_organizations.map((row) => ({
            ...row,
            organization_internal_name: row.organization_internal_name ?? null,
        })),
        outliers: compatible.outliers.map((row) => {
            const legacyRow: LegacyOutlier = row;
            return {
                ...row,
                status: legacyRow.status ?? legacyRow.outcome ?? 'unknown',
            };
        }),
    };
}
